import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus } from 'lucide-react';
import { useTags, type Tag } from '@/hooks/useTags';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';


const TagsTab: React.FC = () => {
  const { tags, loading, createTag, updateTag, deleteTag } = useTags();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingTag) {
      const success = await updateTag(editingTag.id, { ...formData, color: '#3B82F6' });
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createTag({ ...formData, color: '#3B82F6' });
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      is_active: tag.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta tag?')) {
      await deleteTag(id);
    }
  };

  const resetForm = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      is_active: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Tags</h2>
          <p className="text-sm text-neutral-400">Gerencie tags para organizar contatos e oportunidades</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#E4E4E7] text-neutral-900 hover:bg-[#D4D4D8]">
              <Plus className="w-4 h-4 mr-2" />
              Nova Tag
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingTag ? 'Editar Tag' : 'Nova Tag'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name">Nome da Tag</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Tag ativa</Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#E4E4E7] text-neutral-900">
                    {editingTag ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B]">
        {loading ? (
          <div className="p-8 text-center text-neutral-400">Carregando...</div>
        ) : tags.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Nenhuma tag criada</div>
        ) : (
          <div className="divide-y divide-[#3F3F464D]">
            {tags.map((tag) => (
              <div key={tag.id} className="p-4 flex items-center justify-between hover:bg-neutral-800/50">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-neutral-50">{tag.name}</span>
                  {!tag.is_active && (
                    <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-500">
                      Inativa
                    </span>
                  )}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex justify-center items-center rounded-[8px] border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px]">
                      <EllipsisVertical size={20} color="#FAFAFA" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
                    >
                      Excluir
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagsTab;
